import { useMemo, useCallback, useState } from 'react'
import { useQuery } from '@apollo/client'

import { useAdmins } from '../../api/admins'
import { gql } from '../../gql'

import Page from '../../components/Page'
import EmptyList from '../../components/EmptyList'
import { useAdmin } from '../../providers/AdminProvider'
import RadarItem2 from './RadarItem2'
import { JournalStat } from '../../components/ui/journal-stat'
import { EditorStats } from '../../components/ui/editor-stats'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from '../../components/ui/pagination'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { ListRadarsQuery, Radar } from '@/gql/graphql'

const LIMIT = 10

const USER_ID_SARA = 1006
const USER_ID_MONI = 1004
const USER_ID_BARNA = 1000
const USER_ID_C36WAC = 1003

export const LIST_RADARS = gql(`
  query ListRadars($page: Int, $limit: Int) {
    radars(params: { page: $page, limit: $limit }) {
      totalCount
      editorStats {
        unsettledCount
      }
      journalStats {
        journal {
        issueYear
        issueNumber
        issueDate
        gazetteUrl 
        }
        statuses {
          status
          count
        }
      }
      items {
          id
          baseAct {
              lcode
              text
              title
          }
          journal {
              issueYear
              issueNumber
              issueDate
              gazetteUrl
          }
          njtUrl

          digest
          status
          importance
          tags
          enterIntoForce

          affectedCustomers {
            name
            plan
            lists
          }

          delegatedTo {
              id
              name
          }
      }
    }
  }
`)

const ADMIN_STATUS_FILTER: Record<number, string[]> = {
  [USER_ID_SARA]: ['NEW', 'IN_PROGRESS'],
  [USER_ID_C36WAC]: ['NEW', 'IN_PROGRESS'],
  [USER_ID_MONI]: ['NEW', 'IN_PROGRESS', 'DONE', 'PROBLEMS'],
  [USER_ID_BARNA]: ['APPROVED', 'NEW', 'IN_PROGRESS', 'DONE', 'PROBLEMS', 'SENT'],
}

function filterJournalStatsByAdminId(
  admin_id: number,
  stats: NonNullable<ListRadarsQuery['radars']['journalStats']>,
) {
  const allowed = ADMIN_STATUS_FILTER[admin_id]
  if (!allowed) return stats
  return stats
    .map((stat) => ({ ...stat, statuses: stat.statuses.filter((s) => allowed.includes(s.status)) }))
    .filter((stat) => stat.statuses.length > 0)
}

const RadarsPagination = ({
  page,
  totalPages,
  onPageChange,
}: {
  page: number
  totalPages: number
  onPageChange: (p: number) => void
}) => (
  <Pagination>
    <PaginationContent>
      {page > 1 && (
        <PaginationItem>
          <PaginationPrevious
            href="#"
            onClick={(e) => {
              e.preventDefault()
              onPageChange(page - 1)
            }}
          />
        </PaginationItem>
      )}
      {page > 2 && (
        <PaginationItem>
          <PaginationEllipsis />
        </PaginationItem>
      )}
      {[page - 1, page, page + 1]
        .filter((p) => p >= 1 && p <= totalPages)
        .map((p) => (
          <PaginationItem key={p}>
            <PaginationLink
              href="#"
              isActive={p === page}
              onClick={(e) => {
                e.preventDefault()
                onPageChange(p)
              }}
            >
              {p}
            </PaginationLink>
          </PaginationItem>
        ))}
      {page < totalPages - 1 && (
        <PaginationItem>
          <PaginationEllipsis />
        </PaginationItem>
      )}
      <PaginationItem>
        <PaginationNext
          href="#"
          aria-disabled={page === totalPages}
          onClick={(e) => {
            e.preventDefault()
            onPageChange(Math.min(totalPages, page + 1))
          }}
        />
      </PaginationItem>
    </PaginationContent>
  </Pagination>
)

const RadarsPage = () => {
  const { id: admin_id } = useAdmin()
  const { admins } = useAdmins()
  const [page, setPage] = useState(1)

  const { loading, error, data, refetch } = useQuery<ListRadarsQuery>(LIST_RADARS, {
    fetchPolicy: 'no-cache',
    variables: { page, limit: LIMIT },
  })

  const _admins = useMemo(() => {
    return admins.map((admin: any) => ({
      id: admin.id,
      title: admin.name,
    }))
  }, [admins])

  const items = data?.radars.items || []
  const journalStats = useMemo(
    () => filterJournalStatsByAdminId(admin_id, data?.radars.journalStats ?? []),
    [data, admin_id],
  )
  const totalPages = Math.ceil((data?.radars.totalCount ?? 0) / LIMIT)

  return (
    <Page>
      <div className="max-w-4xl mx-auto flex flex-col gap-4 px-4">
        {loading ? (
          'Loading...'
        ) : items.length > 0 ? (
          <>
            <div className="flex gap-2 items-start justify-between">
              <div className="flex flex-wrap gap-2 items-start">
                {journalStats.map((stat) => (
                  <JournalStat
                    key={stat.journal.issueYear + '/' + stat.journal.issueNumber}
                    value={stat}
                  />
                ))}
              </div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <EditorStats
                      unsettledCount={data?.radars.editorStats?.unsettledCount}
                      className="flex-shrink-0"
                    />
                  </TooltipTrigger>
                  <TooltipContent>Nem elszámolt összefoglalók</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <RadarsPagination page={page} totalPages={totalPages} onPageChange={setPage} />
            {items.map((item: Radar) => (
              <RadarItem2 value={item} admins={_admins} open={false} onSave={() => refetch()} />
            ))}
            <RadarsPagination page={page} totalPages={totalPages} onPageChange={setPage} />
          </>
        ) : (
          <div className="mx-auto w-5/6">
            <EmptyList />
          </div>
        )}
      </div>
    </Page>
  )
}

export default RadarsPage
